import React, { useEffect, useState, useRef } from 'react';
import { isNil } from 'lodash';
import { LazyComponentProps, PromiseResult } from './types';

const EmptyComponent = () => null;

const emptyFunction = () => undefined;

const LazyComponent = <T extends object>({
  lazyImport,
  shouldImport = true,
  fallback = <EmptyComponent />,
  placeholder = <EmptyComponent />,
  beforeLoad = emptyFunction,
  afterLoad = emptyFunction,
  onError = emptyFunction,
  componentProps,
}: LazyComponentProps<T>) => {
  const [isLoading, setIsLoading] = useState(true);
  const componentRef = useRef<PromiseResult>();
  const isImported = !isNil(componentRef.current);
  const Component = isImported ? componentRef.current?.default : null;

  useEffect(() => {
    const lazyLoadComponent = async () => {
      try {
        beforeLoad();
        componentRef.current = await lazyImport();
        afterLoad();
      } catch (error) {
        onError(error);
      }

      setIsLoading(false);
    };

    if (shouldImport && !isImported) {
      lazyLoadComponent();
    }
  }, [shouldImport, isImported, beforeLoad, afterLoad, onError, lazyImport]);

  if (isLoading) {
    return placeholder;
  }

  return Component ? <Component {...componentProps} /> : fallback;
};

LazyComponent.displayName = 'LazyComponent';

export default LazyComponent;
